/* eslint-disable @eslint-react/no-children-count,@eslint-react/no-children-map,@eslint-react/no-clone-element */
import React from 'react';

import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';

type BreakdownValueProps = {
  /** The name of the value. */
  name?: string;
  /** The value of the field. */
  value?: React.ReactNode;
  /** The value for data-testid. */
  testId?: string;
  /** Whether the value should be formatted as a currency. */
  isCurrency?: boolean;
  /** Whether the value should be formatted as a percentage. */
  isPercentage?: boolean;
  /** Whether to show ths sign on the value. */
  isChild?: boolean;
  subtract?: boolean;
  children?: React.ReactNode;
} & React.ComponentProps<typeof Box>;

type SignProps = {
  value: string | number;
  subtract?: boolean;
};

const Sign = ({ value, subtract }: Readonly<SignProps>) => {
  const float = Number.parseFloat(String(value));
  if (subtract) {
    return float >= 0 ? <>-</> : <>+</>;
  }
  return float < 0 ? <>-</> : <>+</>;
};

const BreakdownValue = ({
  name,
  value,
  testId,
  isCurrency,
  isPercentage,
  isChild,
  subtract,
  children,
  ...props
}: Readonly<BreakdownValueProps>) => {
  if (value == null || (typeof value === 'number' && Number.isNaN(value))) {
    return null;
  }

  const hasChildren = React.Children.count(children) > 0;
  const fontWeight = hasChildren ? 'bold' : 'normal';
  const showSign = isCurrency && isChild;

  let renderedValue = value;

  if (isCurrency) {
    renderedValue = (
      <>
        {showSign &&
          (typeof value === 'string' || typeof value === 'number') && (
            <Sign value={value} subtract={subtract} />
          )}
        {formatCurrency(
          showSign && (typeof value === 'string' || typeof value === 'number')
            ? Math.abs(Number(value))
            : value,
        )}
      </>
    );
  } else if (isPercentage) {
    renderedValue = <>{value}%</>;
  }

  return (
    <Box {...props}>
      <Flex justifyContent="space-between" as="dl">
        {!isChild && name && (
          <Text fontSize="sm" fontWeight={fontWeight} as="dt">
            {name}
          </Text>
        )}

        <Text
          data-testid={testId}
          fontSize="sm"
          pl={1}
          fontWeight={fontWeight}
          as="dd"
        >
          {renderedValue}
          {isChild && name && <>&nbsp;({name})</>}
        </Text>
      </Flex>
      {children &&
        React.Children.map(children, (child) => {
          return React.isValidElement(child)
            ? React.cloneElement(child, {
                isCurrency,
                isPercentage,
                isChild: true,
                ...child.props,
              })
            : child;
        })}
    </Box>
  );
};
BreakdownValue.defaultValues = {
  isCurrency: false,
  isPercentage: false,
  subtract: false,
};

export default BreakdownValue;
